import {
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type SyntheticEvent,
} from "react"
import { usePhoneInput } from "@/hooks/use-phone-input"
import { cn } from "cn"
import parsePhoneNumber from "libphonenumber-js"

import type {
  EventPhoneAuthProperties,
  PhoneAuthStep,
} from "@/lib/analytics/amplitude-types"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { nowMillis } from "@/lib/date-utils"
import { errorMessage } from "@/lib/exception-utils"
import { formatPhoneAsYouType } from "@/lib/phone-input-utils"
import {
  isPhoneAuthCountry,
  PHONE_AUTH_COUNTRIES_MESSAGE,
  toCountryCode,
  type CountryCode,
} from "@/lib/phone-utils"
import { isSerializedClientError } from "@/lib/storage/api/base-query"
import {
  useSavePhoneSignupEmailMutation,
  useStartPhoneSignInMutation,
  useVerifyPhoneSignInMutation,
  type PhoneVerificationChallenge,
} from "@/lib/storage/api/phone-auth-api"
import {
  selectAuthFlowCallbackUrl,
  selectAuthFlowEmail,
  selectAuthFlowError,
  selectAuthFlowLoading,
  selectLastUsedProvider,
  setAuthFlowEmail,
  setAuthFlowError,
} from "@/lib/storage/auth-slice"
import {
  finishPhoneSignIn,
  handleEmailStepSubmit,
} from "@/lib/storage/auth-thunks"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { AuthFlowHeaders } from "@/components/auth/auth-flow-headers"
import { AuthProviderButtons } from "@/components/auth/auth-provider-buttons"
import IconArrowRight from "@/components/icons/arrow-right"
import { PhoneCountryPicker } from "@/components/phone-country-picker"
import { Button } from "@/components/ui/button"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group"
import { Link } from "@/components/ui/link"
import { OtpField } from "@/components/ui/otp-field"
import { TextInput } from "@/components/ui/text-input"

/** Digits with phone punctuation and no letters or "@", so not an email. */
function looksLikePhone(value: string) {
  return /^\+?[\d\s().-]+$/.test(value.trim()) && /\d/.test(value)
}

/** Digits needed before an entry is treated as a phone number. */
const PHONE_MIN_DIGITS = 7

function phoneAuthErrorType(
  error: unknown
): EventPhoneAuthProperties["properties"]["error_type"] {
  if (isSerializedClientError(error, "ClientValidationException"))
    return "validation"
  if (
    isSerializedClientError(error, "ForbiddenException") ||
    isSerializedClientError(error, "AuthenticationException")
  )
    return "access_denied"
  if (isSerializedClientError(error, "ClientException")) return "api_error"
  return "network_or_unknown"
}

function CodeStatus({
  expired,
  resent,
  verifying,
}: {
  expired: boolean
  resent: boolean
  verifying: boolean
}) {
  let status = "Your code expires after five minutes."
  if (verifying) status = "Checking your code…"
  else if (expired)
    status = "This code has expired. Send a new code to continue."
  else if (resent) status = "We sent a new code. It expires after five minutes."
  return (
    <p role="status" className="-mt-1.5 text-xs text-muted">
      {status}
    </p>
  )
}

function SignInNotice({ className, ...props }: ComponentProps<"p">) {
  return (
    <p className={cn("text-center text-2xs text-muted", className)} {...props}>
      We’ll text or email you a code. By continuing, you agree to our{" "}
      <Link
        href="/terms-of-service"
        target="_blank"
        className="font-medium text-foreground underline"
      >
        Terms of Service
      </Link>{" "}
      and acknowledge our{" "}
      <Link
        href="/privacy-policy"
        target="_blank"
        className="font-medium text-foreground underline"
      >
        Privacy Policy
      </Link>
      .
    </p>
  )
}

/**
 * One field for a phone number or an email. A phone number gets a text code
 * here; an email continues in the email steps of the auth flow.
 */
export function PhoneSignIn({
  surface,
  enablePhoneSignup = false,
}: {
  enablePhoneSignup?: boolean
  surface?: "page" | "dialog"
}) {
  const dispatch = useAppDispatch()
  const callbackUrl = useAppSelector(selectAuthFlowCallbackUrl)
  const authFlowEmail = useAppSelector(selectAuthFlowEmail)
  const emailError = useAppSelector(selectAuthFlowError)
  const isCheckingEmail = useAppSelector(selectAuthFlowLoading)
  const lastUsedProvider = useAppSelector(selectLastUsedProvider)
  // The field takes focus only when the user signed in with it last time,
  // since they may pick Google or Apple instead. In a drawer the dialog
  // focuses it once open, with the keyboard the auth dialog summoned.
  const autoFocusIdentifier =
    lastUsedProvider === "phone" || lastUsedProvider === "email"
  const [startSignIn, { isLoading: isSending }] = useStartPhoneSignInMutation()
  const [verifySignIn, { isLoading: isChecking }] =
    useVerifyPhoneSignInMutation()
  const [saveEmail, { isLoading: isSavingEmail }] =
    useSavePhoneSignupEmailMutation()
  const [registered, setRegistered] = useState(false)
  const [email, setEmail] = useState("")
  const [country, setCountry] = useState<CountryCode>("US")
  // Starts from the auth flow's email so going back from the email steps keeps it.
  const [identifier, setIdentifier] = useState(authFlowEmail)
  // Shown once a phone-like entry reaches PHONE_MIN_DIGITS, and kept while
  // digits are deleted until the field is cleared or holds a non-phone entry.
  const [showCountry, setShowCountry] = useState(false)
  const [identifierFocused, setIdentifierFocused] = useState(false)
  const [challenge, setChallenge] = useState<PhoneVerificationChallenge>()
  const [destination, setDestination] = useState("")
  const [code, setCode] = useState("")
  const [expired, setExpired] = useState(false)
  const [signedIn, setSignedIn] = useState(false)
  const [message, setMessage] = useState<string>()
  const alertId = useId()
  const codeId = useId()
  const [codesSent, setCodesSent] = useState(0)
  const [skipped, setSkipped] = useState(false)
  const busy =
    isSending || isChecking || isSavingEmail || isCheckingEmail || signedIn
  const isPhone = looksLikePhone(identifier)
  const phoneInput = usePhoneInput({
    value: identifier,
    onValueChange: changePhoneNumber,
    defaultCountry: country,
  })

  useEffect(() => {
    if (!challenge) return
    const timer = window.setTimeout(
      () => setExpired(true),
      Math.max(0, challenge.expires_at.epoch_millis - nowMillis())
    )
    return () => window.clearTimeout(timer)
  }, [challenge])

  let authStep: PhoneAuthStep = "phone_number"
  if (registered) authStep = "optional_email"
  else if (challenge) authStep = "code"
  const phoneCountry = destination
    ? (parsePhoneNumber(destination)?.country ?? "unknown")
    : "unknown"
  const lastViewedStep = useRef<PhoneAuthStep | undefined>(undefined)
  useEffect(() => {
    if (lastViewedStep.current === authStep) return
    lastViewedStep.current = authStep
    sendAnalyticsEvent({
      name: "AuthStepView",
      properties: {
        method: "phone",
        ...(surface && { surface }),
        step: authStep,
        phone_country: phoneCountry,
      },
    })
  }, [authStep, phoneCountry, surface])

  function changeIdentifier(event: ChangeEvent<HTMLInputElement>) {
    setMessage(undefined)
    if (emailError) dispatch(setAuthFlowError(undefined))
    const raw = event.target.value
    const phone =
      looksLikePhone(raw) &&
      (showCountry || raw.replace(/\D/g, "").length >= PHONE_MIN_DIGITS)
    setShowCountry(phone)
    if (phone) {
      phoneInput.onChange(event)
      return
    }
    // Typing a letter or "@" after a phone number drops its formatting.
    setIdentifier(showCountry ? raw.replace(/[\s()-]/g, "") : raw)
  }

  // A full international number, typed or pasted, picks its country and
  // shows in that country's national format.
  function changePhoneNumber(value: string) {
    const parsed = value.startsWith("+")
      ? parsePhoneNumber(value, { extract: false })
      : undefined
    // An unsupported country stays as typed, so Continue can explain it.
    const parsedCountry = toCountryCode(parsed?.country)
    if (
      parsed?.isValid() &&
      parsedCountry &&
      isPhoneAuthCountry(parsedCountry)
    ) {
      setCountry(parsedCountry)
      setIdentifier(parsed.formatNational())
      return
    }
    // A complete national number shows in the country's national format, so
    // a US number typed with its leading 1 doesn't repeat the +1 beside it.
    const national = value.startsWith("+")
      ? undefined
      : parsePhoneNumber(value, { defaultCountry: country, extract: false })
    setIdentifier(national?.isValid() ? national.formatNational() : value)
  }

  function changeCountry(value: CountryCode) {
    setCountry(value)
    setIdentifier(
      formatPhoneAsYouType(identifier.replace(/[^\d+]/g, ""), "", value)
    )
    phoneInput.ref.current?.focus()
  }

  function submitIdentifier(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault()
    if (busy) return
    if (isPhone) {
      void sendCode()
      return
    }
    setMessage(undefined)
    const email = identifier.trim()
    if (!email) {
      setMessage("Enter a phone number or email.")
      return
    }
    if (!email.includes("@")) {
      setMessage("Enter a valid phone number or email.")
      return
    }
    sendAnalyticsEvent({
      name: "PhoneAuthUseEmail",
      properties: { step: "phone_number", phone_country: "unknown" },
    })
    dispatch(setAuthFlowEmail(email))
    void dispatch(handleEmailStepSubmit())
  }

  async function sendCode() {
    setMessage(undefined)
    const phone = parsePhoneNumber(identifier, {
      defaultCountry: country,
      extract: false,
    })
    const destinationCountry = phone?.country ?? "unknown"
    sendAnalyticsEvent({
      name: "PhoneAuthSubmit",
      properties: { step: "phone_number", phone_country: destinationCountry },
    })
    if (!phone?.isValid() || phone.ext) {
      sendAnalyticsEvent({
        name: "PhoneAuthResult",
        properties: {
          step: "phone_number",
          phone_country: destinationCountry,
          outcome: "failure",
          error_type: "invalid_number",
        },
      })
      setMessage("Enter a valid phone number without an extension.")
      return
    }
    if (!isPhoneAuthCountry(phone.country)) {
      sendAnalyticsEvent({
        name: "PhoneAuthResult",
        properties: {
          step: "phone_number",
          phone_country: destinationCountry,
          outcome: "failure",
          error_type: "unsupported_country",
        },
      })
      setMessage(PHONE_AUTH_COUNTRIES_MESSAGE)
      return
    }
    try {
      const created = await startSignIn({
        phone_number: phone.number,
        ...(enablePhoneSignup && { enable_phone_signup: true }),
      }).unwrap()
      sendAnalyticsEvent({
        name: "PhoneAuthResult",
        properties: {
          step: "phone_number",
          phone_country: destinationCountry,
          outcome: "success",
        },
      })
      setChallenge(created)
      setDestination(phone.number)
      setCode("")
      setExpired(false)
      setCodesSent((count) => count + 1)
    } catch (error) {
      sendAnalyticsEvent({
        name: "PhoneAuthResult",
        properties: {
          step: "phone_number",
          phone_country: destinationCountry,
          outcome: "failure",
          error_type: phoneAuthErrorType(error),
        },
      })
      setMessage(
        errorMessage(error, "We couldn't send a code. Please try again.")
      )
    }
  }

  async function checkCode(otp: string) {
    if (!challenge || busy) return
    sendAnalyticsEvent({
      name: "PhoneAuthSubmit",
      properties: { step: "code", phone_country: phoneCountry },
    })
    if (nowMillis() >= challenge.expires_at.epoch_millis) {
      sendAnalyticsEvent({
        name: "PhoneAuthResult",
        properties: {
          step: "code",
          phone_country: phoneCountry,
          outcome: "failure",
          error_type: "expired",
        },
      })
      setExpired(true)
      return
    }
    setMessage(undefined)
    try {
      const result = await verifySignIn({
        challenge_token: challenge.challenge_token,
        otp,
      }).unwrap()
      sendAnalyticsEvent({
        name: result.registration_occurred ? "RegisterUser" : "SignIn",
        properties: { method: "phone", phone_country: phoneCountry },
      })
      if (result.registration_occurred) {
        setRegistered(true)
      } else {
        await completeSignIn(false)
      }
    } catch (error) {
      sendAnalyticsEvent({
        name: "PhoneAuthResult",
        properties: {
          step: "code",
          phone_country: phoneCountry,
          outcome: "failure",
          error_type: phoneAuthErrorType(error),
        },
      })
      setMessage(
        errorMessage(error, "We couldn't sign you in. Please try again.")
      )
      // Clears the boxes so typing the code again checks it again.
      setCode("")
      document.getElementById(codeId)?.focus()
    }
  }

  async function completeSignIn(registrationOccurred: boolean) {
    setSignedIn(true)
    setMessage(undefined)
    try {
      await dispatch(
        finishPhoneSignIn({ callbackUrl, registrationOccurred })
      ).unwrap()
    } catch (error) {
      setSignedIn(false)
      setMessage(errorMessage(error, "We couldn't continue. Please try again."))
    }
  }

  async function addEmail(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault()
    if (busy) return
    if (!email.trim()) {
      setMessage("Enter an email, or skip for now.")
      return
    }
    setMessage(undefined)
    sendAnalyticsEvent({
      name: "PhoneAuthSubmit",
      properties: { step: "optional_email", phone_country: phoneCountry },
    })
    try {
      await saveEmail(email.trim()).unwrap()
      sendAnalyticsEvent({
        name: "PhoneAuthResult",
        properties: {
          step: "optional_email",
          phone_country: phoneCountry,
          outcome: "success",
        },
      })
      await completeSignIn(true)
    } catch (error) {
      sendAnalyticsEvent({
        name: "PhoneAuthResult",
        properties: {
          step: "optional_email",
          phone_country: phoneCountry,
          outcome: "failure",
          error_type: phoneAuthErrorType(error),
        },
      })
      setMessage(
        errorMessage(
          error,
          "We couldn't save your email. You can skip this for now."
        )
      )
    }
  }

  if (registered) {
    return (
      <>
        <AuthFlowHeaders title="Add your email" />
        <form
          onSubmit={(event) => void addEmail(event)}
          // Fills the auth flow's min height, so mt-auto pins Skip to the
          // bottom.
          className="flex flex-1 flex-col gap-3"
        >
          <p className="-mt-3 mb-3 text-center leading-snug text-pretty">
            Your phone is verified.
            <br />
            <span className="text-muted">
              Add an email for confirmations and updates.
            </span>
          </p>
          <TextInput
            type="email"
            autoComplete="email"
            aria-label="Email (optional)"
            placeholder="Email"
            maxLength={254}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              setMessage(undefined)
            }}
            aria-invalid={message ? true : undefined}
            aria-describedby={message ? alertId : undefined}
            disabled={busy}
            autoFocus
            size="lg"
          />
          {message && (
            <p
              id={alertId}
              role="alert"
              className="-mt-1.5 text-xs text-destructive"
            >
              {message}
            </p>
          )}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={isSavingEmail || (signedIn && !skipped)}
          >
            Continue
          </Button>
          <Button
            type="button"
            variant="link"
            size="lg"
            className="mx-auto mt-auto no-underline"
            loading={signedIn && skipped}
            disabled={busy}
            onClick={() => {
              sendAnalyticsEvent({
                name: "PhoneAuthEmailSkipped",
                properties: {
                  step: "optional_email",
                  phone_country: phoneCountry,
                },
              })
              setSkipped(true)
              void completeSignIn(true)
            }}
          >
            Skip for now
          </Button>
        </form>
      </>
    )
  }

  const alert = message ?? (challenge ? undefined : emailError)
  // Sits above each form's submit button, next to the field it's about.
  const alertMessage = alert && (
    <p id={alertId} role="alert" className="-mt-1.5 text-xs text-destructive">
      {alert}
    </p>
  )
  const showNotice = identifierFocused || identifier !== ""
  const destinationPhone = destination
    ? parsePhoneNumber(destination)
    : undefined
  // "+1 (201) 555-0123" for North American numbers. Elsewhere the national
  // format can start with a trunk 0 that never follows the calling code.
  const destinationLabel =
    destinationPhone?.countryCallingCode === "1"
      ? `+1 ${destinationPhone.formatNational()}`
      : (destinationPhone?.formatInternational() ?? destination)

  return (
    <>
      <AuthFlowHeaders title={challenge ? "Confirm it’s you" : undefined} />
      {!challenge && (
        <form onSubmit={submitIdentifier} className="space-y-3">
          <InputGroup size="lg">
            {/* A number typed with "+" already carries its calling code. */}
            {showCountry && !identifier.startsWith("+") && (
              <InputGroupAddon>
                <PhoneCountryPicker
                  value={country}
                  onValueChange={changeCountry}
                  disabled={busy}
                />
              </InputGroupAddon>
            )}
            <InputGroupInput
              ref={phoneInput.ref}
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              aria-label="Phone number or email"
              placeholder="Phone number or email"
              maxLength={254}
              value={identifier}
              onChange={changeIdentifier}
              onFocus={() => setIdentifierFocused(true)}
              onBlur={() => setIdentifierFocused(false)}
              aria-invalid={alert ? true : undefined}
              aria-describedby={alert ? alertId : undefined}
              disabled={busy}
              autoFocus={autoFocusIdentifier}
            />
          </InputGroup>
          {alertMessage}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={isSending || isCheckingEmail}
          >
            Continue
          </Button>
          {showNotice && (
            // Keeps focus in the field, so the links don't move out from
            // under a click on an empty field.
            <SignInNotice onMouseDown={(event) => event.preventDefault()} />
          )}
        </form>
      )}
      {challenge && (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            void checkCode(code)
          }}
          // Fills the auth flow's min height, so mt-auto pins the last button
          // to the bottom.
          className="flex flex-1 flex-col items-center gap-5 text-center"
        >
          <p className="text-muted">
            We sent a code to
            <span className="no-translate mt-1 block font-medium text-foreground">
              {destinationLabel}
            </span>
          </p>
          <label htmlFor={codeId} className="sr-only">
            Verification code
          </label>
          <OtpField
            id={codeId}
            length={6}
            value={code}
            onValueChange={(value) => {
              setCode(value)
              setMessage(undefined)
            }}
            onValueComplete={(value) => void checkCode(value)}
            // Stays enabled while a code is checked, so a wrong code can
            // hand focus back to the first box; checkCode ignores repeats.
            disabled={expired || signedIn}
            invalid={Boolean(alert)}
            autoFocus
          />
          {alertMessage ?? (
            <CodeStatus
              expired={expired}
              resent={codesSent > 1}
              verifying={isChecking || signedIn}
            />
          )}
          <p className="text-sm">
            Didn’t get it?{" "}
            <button
              type="button"
              className="font-semibold underline disabled:opacity-50"
              disabled={busy}
              onClick={() => void sendCode()}
            >
              Send a new code
            </button>
          </p>
          <Button
            type="button"
            variant="subtle"
            size="lg"
            block
            className="mt-auto"
            disabled={busy}
            iconBefore={{ icon: IconArrowRight, className: "rotate-180" }}
            onClick={() => {
              sendAnalyticsEvent({
                name: "PhoneAuthRestart",
                properties: { step: "code", phone_country: phoneCountry },
              })
              setChallenge(undefined)
              setCode("")
              setMessage(undefined)
            }}
          >
            Use a different number or email
          </Button>
        </form>
      )}
      {!challenge && <AuthProviderButtons />}
    </>
  )
}
